import { useState } from 'react'
import { Container, CircularProgress } from '@mui/material'
import Spacebox from '../components/Spacebox'
import Flexbox from '../components/Flexbox'
import { formatUSPhoneNumber } from '../utilities/utils'
import { useSelector } from 'react-redux'

const WORKFLOW_NAME = 'v4 - Api Transfer FDR Call'

const RemoveWorkflowPage = ({ title }) => {
    document.querySelector('title').innerHTML = title

    const mode = useSelector(state => state.darkmode.value)

    const [query, setQuery] = useState('')
    const [searching, setSearching] = useState(false)
    const [searchError, setSearchError] = useState('')
    const [user, setUser] = useState(null)

    const [removing, setRemoving] = useState(false)
    const [removeResult, setRemoveResult] = useState(null) // { success, msg }

    const border = mode.dark ? '#3a3a3a' : '#e4e6eb'
    const shade = mode.dark ? '#2a2a2a' : '#f9fafb'
    const headBg = mode.dark ? '#1e1e1e' : '#f0f2f5'

    const handleSearch = async (e) => {
        e.preventDefault()
        if (!query.trim()) return
        setSearching(true)
        setSearchError('')
        setUser(null)
        setRemoveResult(null)
        try {
            const res = await fetch(`/api/users/${encodeURIComponent(query.trim())}`, { credentials: 'include' })
            const json = await res.json()
            if (!json.success) throw new Error(json.msg || 'Contact not found')
            console.log(json.data)
            setUser(json.data)
        } catch (err) {
            setSearchError(err.message)
        } finally {
            setSearching(false)
        }
    }

    const handleRemove = async () => {
        if (!user) return
        setRemoving(true)
        setRemoveResult(null)
        try {
            const res = await fetch(`/api/users/${encodeURIComponent(user.contact_id)}/remove-atfc-workflow`, { credentials: 'include' })
            const json = await res.json()
            setRemoveResult({
                success: json.success,
                msg: json.success
                    ? `${user.first_name} ${user.last_name} has been removed from "${WORKFLOW_NAME}".`
                    : json.msg || 'Server returned an error.'
            })
        } catch (err) {
            setRemoveResult({ success: false, msg: err.message })
        } finally {
            setRemoving(false)
        }
    }

    return (
        <div style={{ minHeight: '100dvh', paddingBottom: 60 }}>
            <Container maxWidth="md">
                <Spacebox padding="20px" />

                <p style={{ fontWeight: 700, fontSize: '20px', margin: 0 }}>Remove from Workflow</p>
                <small style={{ opacity: .5 }}>Search a contact by ID and remove them from <strong>{WORKFLOW_NAME}</strong>.</small>

                <Spacebox padding="20px" />

                {/* ── search ── */}
                <form onSubmit={handleSearch}>
                    <Flexbox alignItems="center" style={{ gap: 8 }}>
                        <input
                            value={query}
                            onChange={e => { setQuery(e.target.value); setSearchError(''); setUser(null); setRemoveResult(null) }}
                            placeholder="Enter Contact ID"
                            style={{
                                flex: 1, padding: '10px 14px', borderRadius: 8,
                                border: `1px solid ${searchError ? '#ef4444' : border}`,
                                background: shade, fontSize: '14px',
                                outline: 'none', color: 'inherit'
                            }}
                        />
                        <button
                            type="submit"
                            disabled={searching || !query.trim()}
                            style={{
                                padding: '10px 22px', borderRadius: 8, border: 'none',
                                background: '#0067CF', color: 'white', fontWeight: 600,
                                cursor: searching || !query.trim() ? 'not-allowed' : 'pointer',
                                opacity: !query.trim() ? .5 : 1, fontSize: '14px',
                                display: 'flex', alignItems: 'center', gap: 8,
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {searching && <CircularProgress size={14} style={{ color: 'white' }} />}
                            {searching ? 'Searching…' : 'Search'}
                        </button>
                    </Flexbox>
                    {searchError && (
                        <small style={{ color: '#ef4444', marginTop: 6, display: 'block' }}>{searchError}</small>
                    )}
                </form>

                {/* ── results table ── */}
                {user && (
                    <>
                        <Spacebox padding="24px" />
                        <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>

                            {/* table header */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1.5fr 1.8fr 1.4fr .8fr 1.2fr .8fr 1.4fr',
                                background: headBg,
                                borderBottom: `1px solid ${border}`,
                                padding: '10px 16px'
                            }}>
                                {['Name', 'Email', 'Phone', 'State', 'Buyer', 'Status', 'Action'].map(col => (
                                    <div key={col} style={{ fontSize: '11px', fontWeight: 700, opacity: .5, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                                        {col}
                                    </div>
                                ))}
                            </div>

                            {/* table row */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1.5fr 1.8fr 1.4fr .8fr 1.2fr .8fr 1.4fr',
                                padding: '14px 16px',
                                alignItems: 'center'
                            }}>
                                <div style={{ fontSize: '14px', fontWeight: 600 }}>
                                    {user.first_name} {user.last_name}
                                </div>
                                <div style={{ fontSize: '13px', opacity: .75, wordBreak: 'break-all', paddingRight: 8 }}>
                                    {user.email}
                                </div>
                                <div style={{ fontSize: '13px', opacity: .75 }}>
                                    {formatUSPhoneNumber(String(user.phone || ''))}
                                </div>
                                <div style={{ fontSize: '13px', opacity: .75 }}>
                                    {user.state || '—'}
                                </div>
                                <div style={{ fontSize: '13px', opacity: .75 }}>
                                    {user.buyer || 'Unassigned'}
                                </div>
                                <div>
                                    <span style={{
                                        fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: 100,
                                        background: user.completed ? '#22c55e20' : '#ef444420',
                                        color: user.completed ? '#16a34a' : '#dc2626',
                                        border: `1px solid ${user.completed ? '#22c55e40' : '#ef444440'}`
                                    }}>
                                        {user.completed ? 'Completed' : 'Pending'}
                                    </span>
                                </div>
                                <div>
                                    <button
                                        onClick={handleRemove}
                                        disabled={removing || removeResult?.success}
                                        style={{
                                            padding: '7px 12px', borderRadius: 7,
                                            background: removeResult?.success ? '#22c55e20' : '#ef444415',
                                            color: removeResult?.success ? '#16a34a' : '#dc2626',
                                            fontWeight: 600, fontSize: '12px',
                                            cursor: removing || removeResult?.success ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            opacity: removing ? .6 : 1,
                                            border: `1px solid ${removeResult?.success ? '#22c55e40' : '#ef444430'}`
                                        }}
                                    >
                                        {removing && <CircularProgress size={11} style={{ color: '#dc2626' }} />}
                                        {removeResult?.success ? '✓ Removed' : removing ? 'Removing…' : 'Remove'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* ── result message ── */}
                        {removeResult && (
                            <>
                                <Spacebox padding="14px" />
                                <div style={{
                                    borderRadius: 8, padding: '12px 16px',
                                    background: removeResult.success ? '#f0fdf4' : '#fef2f2',
                                    border: `1px solid ${removeResult.success ? '#86efac' : '#fca5a5'}`,
                                    display: 'flex', gap: 10, alignItems: 'center'
                                }}>
                                    <span style={{ fontSize: '18px' }}>{removeResult.success ? '✅' : '❌'}</span>
                                    <span style={{
                                        fontSize: '14px', fontWeight: 600,
                                        color: removeResult.success ? '#15803d' : '#dc2626'
                                    }}>
                                        {removeResult.msg}
                                    </span>
                                </div>
                            </>
                        )}
                    </>
                )}
            </Container>
        </div>
    )
}

export default RemoveWorkflowPage
